import { Link } from "@inertiajs/react"
import logo from "/images/Logo Mark - Black.svg"
import type { ParseKeys } from "i18next"
import {
  ChevronDown,
  House,
  LayoutGrid,
  LogOut,
  LucideIcon,
  Search,
  ShieldCheck,
  Undo2,
} from "lucide-react"
import { useId } from "react"
import { useTranslation } from "react-i18next"

import { useXsrfToken } from "@/components/hooks/useXsrfToken"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { buttonVariants } from "@/components/ui/button-variants"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  useWorkspaceDefinition,
  useWorkspaceDefinitions,
  workspaceDefinition,
  workspaceLinks,
  type WorkspaceDefinition,
  type WorkspaceLink,
} from "@/components/workspace/definition"
import { ROUTES } from "@/constants"
import { cn } from "@/lib/utils"
import { workspace as ownWorkspace } from "@/routes"
import { HeldWorkspace, User, useSharedProps } from "@/sharedProps"

// The top row on every page: the logo mark, the menu of the signed-in
// user's workspaces (and an administrator's Admin pill), the Search link, a
// filler, then the user chip, an administrator's Back to admin and Logout,
// under a 3px ink rule that keeps the row apart from the page. The
// rule sits inside the row's 56px. Below `sm` the labels give way to icons,
// which keep their accessible names, and the filler collapses first. The
// menu lists the workspaces the user holds, whatever page they are on: it is
// how a user with several roles moves between them. The logo leads to
// /workspace, which redirects to the user's first workspace. Each
// workspace's mark, root and destinations come from its owner type's
// definition, which the composition root provides; the header imports no
// domain.
export default function AppHeader() {
  const { t } = useTranslation("nav")
  const { authenticated, user, workspaces, impersonating } = useSharedProps()

  return (
    <header className="bg-brand-background flex min-h-14 flex-col border-t-[3px] border-black">
      <div className="container mx-auto flex flex-1 items-center gap-2 px-4">
        <nav
          aria-label={t("main")}
          className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2"
        >
          <Link
            href={!authenticated ? ROUTES.LOGIN : ownWorkspace().url}
            className="shrink-0"
          >
            <img src={logo} alt={t("logo")} className="h-10 w-auto" />
          </Link>
          {authenticated ? (
            <>
              <WorkspacesMenu workspaces={workspaces} />
              {user.admin && <AdminPill />}
              <HeaderLink
                href={ROUTES.SEARCH}
                Icon={Search}
                label={t("search")}
              />
              <div
                aria-hidden
                data-testid="header-filler"
                className="bg-brand-wise-green hidden h-9 min-w-4 flex-1 rounded-full sm:block"
              />
            </>
          ) : (
            <HeaderLink href={ROUTES.LOGIN} Icon={House} label={t("home")} />
          )}
        </nav>
        {authenticated && (
          <UserMenu user={user} impersonating={impersonating} />
        )}
      </div>
    </header>
  )
}

function HeaderLink({
  href,
  Icon,
  label,
}: {
  href: string
  Icon: LucideIcon
  label: string
}) {
  return (
    <Link
      href={href}
      aria-label={label}
      className={buttonVariants({
        variant: "link",
        size: "sm",
        className: "shrink-0 px-2 no-underline sm:px-3",
      })}
    >
      <Icon />
      <span className="hidden sm:inline">{label}</span>
    </Link>
  )
}

const ROLE_PILL =
  "font-control inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-2.5 text-sm text-black sm:px-3"

// The highlight is the brand light grey with the text in ink: the
// dropdown's default highlight is the accent token, brand dark grey, which
// swallows the description.
const MENU_CELL =
  "focus:bg-brand-light-grey focus:text-foreground h-full items-start rounded-md p-2"

// The menu of the workspaces the signed-in user holds. None (an
// administrator with no role) shows nothing. One is a pill naming its role,
// several a Workspaces pill. Either way the menu holds one section per
// workspace, led by a link to the workspace's page named as its title names
// it ("Test Teacher One's Workspace"), then its destinations, the same the
// workspace page lists, in the same order, as equal-height cells in two
// columns from `sm` up.
function WorkspacesMenu({ workspaces }: { workspaces: HeldWorkspace[] }) {
  const { t } = useTranslation("nav")
  const definitions = useWorkspaceDefinitions()

  if (workspaces.length === 0) return null

  const only =
    workspaces.length === 1
      ? workspaceDefinition(definitions, workspaces[0].ownerType)
      : null
  const Icon = only === null ? LayoutGrid : only.icon
  const label =
    only === null ? t("workspaces") : t(`roles.${workspaces[0].ownerType}`)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t("role_menu", { role: label })}
        data-testid="role-menu"
        className={cn(
          ROLE_PILL,
          "focus-visible:ring-ring cursor-pointer hover:brightness-95 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden",
          only === null ? "bg-white" : only.tint,
        )}
      >
        <Icon className="size-4" />
        <span className="hidden sm:inline">{label}</span>
        <ChevronDown className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="flex w-[min(36rem,calc(100vw-1rem))] flex-col gap-1 p-2"
      >
        {workspaces.map((workspace, index) => (
          <WorkspaceSection
            key={`${workspace.ownerType}-${workspace.ownerId}`}
            workspace={workspace}
            separated={index > 0}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function WorkspaceSection({
  workspace,
  separated,
}: {
  workspace: HeldWorkspace
  separated: boolean
}) {
  const { t } = useTranslation("nav")
  const definition = useWorkspaceDefinition(workspace.ownerType)
  const Icon = definition.icon
  const title = t(`${workspace.ownerType}_workspace_title`, {
    name: workspace.name ?? "",
  })

  return (
    <>
      {separated && <DropdownMenuSeparator />}
      <DropdownMenuGroup aria-label={title} className="flex flex-col gap-1">
        <DropdownMenuItem asChild className={cn(MENU_CELL, "items-center")}>
          <Link
            href={definition.root(workspace.ownerId)}
            data-testid="workspace-section-root"
          >
            <span
              className={cn(
                "inline-flex size-7 items-center justify-center rounded-full",
                definition.tint,
              )}
            >
              <Icon className="text-foreground" />
            </span>
            <span className="font-control">{title}</span>
          </Link>
        </DropdownMenuItem>
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          {workspaceLinks(definition, workspace.ownerId).map((link) => (
            <DestinationItem
              key={link.key}
              definition={definition}
              link={link}
            />
          ))}
        </div>
      </DropdownMenuGroup>
    </>
  )
}

function DestinationItem({
  definition,
  link,
}: {
  definition: WorkspaceDefinition
  link: WorkspaceLink
}) {
  const { t } = useTranslation(definition.namespaces)
  const titleId = useId()
  const descriptionId = useId()
  const body = (
    <>
      <link.icon className="text-foreground mt-0.5" />
      <span className="flex min-w-0 flex-col">
        <span id={titleId} className="font-control">
          {t(link.titleKey as ParseKeys)}
        </span>
        <span
          id={descriptionId}
          className="text-foreground/70 text-xs whitespace-normal"
        >
          {t(link.descriptionKey as ParseKeys)}
        </span>
      </span>
    </>
  )
  const labelling = {
    "aria-labelledby": titleId,
    "aria-describedby": descriptionId,
  }

  if (link.href === null) {
    return (
      <DropdownMenuItem disabled className={MENU_CELL} {...labelling}>
        {body}
      </DropdownMenuItem>
    )
  }

  return (
    <DropdownMenuItem asChild className={MENU_CELL} {...labelling}>
      <Link href={link.href}>{body}</Link>
    </DropdownMenuItem>
  )
}

// The first letters of the first and last words of a name, "Test Teacher
// One" as "TO", for an avatar with no photo.
function initialsOf(name: string) {
  const words = name.split(/\s+/).filter(Boolean)
  if (words.length < 2) return words.map((word) => word[0]).join("")
  return `${words[0][0]}${words[words.length - 1][0]}`
}

// An administrator signed in as themselves, placed with the role pills but
// with no menu: the admin pages are reached by Back to admin. Impersonating,
// the user is the impersonated one, who is never an administrator.
function AdminPill() {
  const { t } = useTranslation("nav")

  return (
    <span data-testid="admin-status" className={cn(ROLE_PILL, "bg-white")}>
      <ShieldCheck aria-hidden className="size-4" />
      <span className="sr-only sm:not-sr-only">{t("admin")}</span>
    </span>
  )
}

const BACK_TO_ADMIN = buttonVariants({
  variant: "link",
  size: "sm",
  className: "shrink-0 px-2 no-underline sm:px-3",
})

// Back to admin, in the same place and style whether or not the
// administrator is impersonating someone. Impersonating, it ends the
// impersonation; signed in as themselves, it leads to the admin pages.
// Anyone else, nothing.
function BackToAdmin({
  user,
  impersonating,
}: {
  user: User
  impersonating: boolean
}) {
  const { t } = useTranslation("nav")
  const token = useXsrfToken()
  const content = (
    <>
      <Undo2 />
      <span className="hidden sm:inline">{t("back_to_admin")}</span>
    </>
  )

  if (impersonating) {
    return (
      <form action={ROUTES.IMPERSONATION} method="post">
        <input type="hidden" name="_method" value="delete" />
        <input type="hidden" name="authenticity_token" value={token} />
        <button
          type="submit"
          aria-label={t("back_to_admin")}
          className={BACK_TO_ADMIN}
        >
          {content}
        </button>
      </form>
    )
  }
  if (!user.admin) return null

  return (
    <a
      href={ROUTES.ADMIN}
      aria-label={t("back_to_admin")}
      className={BACK_TO_ADMIN}
    >
      {content}
    </a>
  )
}

function UserMenu({
  user,
  impersonating,
}: {
  user: User
  impersonating: boolean
}) {
  const { t } = useTranslation("nav")

  return (
    <nav
      aria-label={t("menu_for", { name: user.full_name })}
      className="flex shrink-0 items-center gap-1"
    >
      <Link
        href="/users/edit"
        aria-label={t("account")}
        className="bg-brand-light-sagecool-blue hover:bg-brand-sagecool-blue font-control inline-flex h-9 items-center gap-2 rounded-full p-1 text-sm no-underline sm:pr-3"
      >
        <Avatar className="size-7 border">
          <AvatarImage src={user.avatar_url ?? undefined} alt="" />
          <AvatarFallback className="text-xs">
            {initialsOf(user.full_name)}
          </AvatarFallback>
        </Avatar>
        <span className="hidden sm:inline">{user.full_name}</span>
      </Link>
      <BackToAdmin user={user} impersonating={impersonating} />
      <LogoutForm />
    </nav>
  )
}

function LogoutForm() {
  const { t } = useTranslation("nav")
  const token = useXsrfToken()

  return (
    <form action="/users/sign_out" method="post">
      <input type="hidden" name="_method" value="delete" />
      <input type="hidden" name="authenticity_token" value={token} />
      <Button
        type="submit"
        variant="link"
        size="sm"
        aria-label={t("logout")}
        className="px-2 no-underline sm:px-3"
      >
        <LogOut />
        <span className="hidden sm:inline">{t("logout")}</span>
      </Button>
    </form>
  )
}
